import type { LifetimeConfigParams, LifetimeParameters } from '@hub/bearing-hub/common/types';
import { lifetimeConfigParams } from '@hub/bearing-hub/common/constants';
import { isObject } from '@hub/libs/utils';
import type {
  IbhLifetimePartLinearAndPivotingRequestDto,
  IbhLifetimePartLinearAndRotationRequestDto,
  IbhLifetimePartLinearRequestDto,
  IbhLifetimePartPivotingRequestDto,
  IbhLifetimePartRotationRequestDto,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { IbhUnitSystem } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { createLifetimeConfigPayload } from '@hub/bearing-hub/libs/BearingsLifetime/BearingLifetimeFactory';
import { createContext, type ReactNode, useContext, useRef } from 'react';
import {
  DefaultServiceLifeForBearings,
  DefaultServiceLifeForBearingsForUS,
} from '@hub/bearing-hub/libs/BearingsLifetime/constants';
import type { StoreApi } from 'zustand';
import { createStore, useStore } from 'zustand';

export type LifetimeConfig =
  | IbhLifetimePartLinearRequestDto
  | IbhLifetimePartPivotingRequestDto
  | IbhLifetimePartRotationRequestDto
  | IbhLifetimePartLinearAndPivotingRequestDto
  | IbhLifetimePartLinearAndRotationRequestDto;

export interface IbhLifetimeValuesStore {
  lifetimeParameters: LifetimeParameters;
  setLifetimeParameter: (lifetimeParameter: LifetimeParameters) => void;
  setLifetimeParametersValue: (key: LifetimeConfigParams, value: number | string | undefined) => void;
  hasLifetimeParameterValues: () => boolean;
  getTransformedLifetimeParameters: () => LifetimeParameters;
  resetLifetimeParametersToDefault: (unitSystem: IbhUnitSystem, query?: string) => void;
  getLifetimeConfigPayload: (unitSystem: IbhUnitSystem, flatStructure?: boolean) => LifetimeConfig;
}

export const getDefaultLifetimeParameters = (unitSystem: IbhUnitSystem): LifetimeParameters =>
  unitSystem === IbhUnitSystem.Metric
    ? { ...DefaultServiceLifeForBearings, force: DefaultServiceLifeForBearings.load.force }
    : { ...DefaultServiceLifeForBearingsForUS, force: DefaultServiceLifeForBearingsForUS.load.force };

const transformValueToProperType = (lifetimeParameters: LifetimeParameters) => {
  const expectedString = [
    lifetimeConfigParams.environment,
    lifetimeConfigParams.oppositeMaterialId,
    lifetimeConfigParams.temporalTypeOfMovement,
    lifetimeConfigParams.intermittentParams,
    lifetimeConfigParams.typeOfMovement,
    lifetimeConfigParams.typeOfOperation,
  ] as LifetimeConfigParams[];

  let transformedParameters = lifetimeParameters;

  Object.keys(lifetimeParameters).forEach((key: keyof LifetimeParameters) => {
    if (isObject(lifetimeParameters[key])) {
      return;
    }

    transformedParameters = {
      ...transformedParameters,
      [key]: expectedString.includes(key)
        ? lifetimeParameters[key].toString()
        : parseFloat(lifetimeParameters[key] as string),
    };
  });

  return transformedParameters;
};

export const createLifetimeParametersStore = (initUnitSystem: IbhUnitSystem) =>
  createStore<IbhLifetimeValuesStore>((set, get) => ({
    lifetimeParameters: getDefaultLifetimeParameters(initUnitSystem),
    setLifetimeParameter: lifetimeParameter =>
      set(state => ({
        lifetimeParameters: { ...state.lifetimeParameters, ...lifetimeParameter },
      })),

    setLifetimeParametersValue: (key, value) =>
      set(state => ({
        lifetimeParameters: { ...state.lifetimeParameters, [key]: value },
      })),

    hasLifetimeParameterValues: () => Object.keys(get().lifetimeParameters).length > 0,

    getTransformedLifetimeParameters: () => transformValueToProperType(get().lifetimeParameters),

    resetLifetimeParametersToDefault: (unitSystem: IbhUnitSystem, query?: Partial<LifetimeParameters>) =>
      set({ lifetimeParameters: { ...getDefaultLifetimeParameters(unitSystem), ...query } }),

    getLifetimeConfigPayload: (unitSystem = IbhUnitSystem.Metric, flatStructure = false) => {
      const payload = createLifetimeConfigPayload(get().getTransformedLifetimeParameters(), unitSystem, flatStructure);
      const typeOfMovement = Object.keys(payload)[0];

      return payload[typeOfMovement] as LifetimeConfig;
    },
  }));

const LifetimeParametersStoreContext = createContext<StoreApi<IbhLifetimeValuesStore> | null>(null);

interface LifetimeParametersProviderProps {
  unitSystem: IbhUnitSystem;
  children: ReactNode;
}

export const LifetimeParametersStoreProvider = ({ unitSystem, children }: LifetimeParametersProviderProps) => {
  const storeRef = useRef<StoreApi<IbhLifetimeValuesStore>>();
  if (!storeRef.current) {
    storeRef.current = createLifetimeParametersStore(unitSystem);
  }

  return (
    <LifetimeParametersStoreContext.Provider value={storeRef.current}>
      {children}
    </LifetimeParametersStoreContext.Provider>
  );
};

export const useLifetimeParametersStore = <T = IbhLifetimeValuesStore,>(
  selector?: (state: IbhLifetimeValuesStore) => T
): T => {
  const store = useContext(LifetimeParametersStoreContext);
  if (!store) {
    throw new Error('useLifetimeParametersStore must be used inside LifetimeParametersStoreProvider');
  }
  return useStore(store, selector ?? (s => s as unknown as T));
};
